import React from 'react';

const CustomSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "",
  className = "",
  error = "",
  required = false,
  disabled = false,
  size = "md", // sm, md, lg
  variant = "default", // default, minimal
  icon: Icon = null,
  id,
  name,
  ...props
}) => {
  // Size configurations
  const sizeClasses = {
    sm: "py-1.5 pl-2 pr-7 text-sm",
    md: "py-2.5 pl-3 pr-8 text-sm",
    lg: "py-3 pl-4 pr-10 text-base"
  };

  // Variant configurations
  const variantClasses = {
    default: "border-0 bg-gray-50 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all",
    minimal: "border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
    input: "border border-gray-300 rounded-md bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
  };

  // Icon sizes and positioning
  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5"
  };

  const selectClasses = `
    block w-full text-gray-900 focus:outline-none appearance-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${Icon ? 'pl-8' : ''}
    ${error ? 'ring-2 ring-red-500 bg-red-50' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div>
      {label && (
        <label htmlFor={id || name} className="block text-sm font-semibold text-gray-800 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
            <Icon className={`text-gray-400 ${iconSizes[size]}`} />
          </div>
        )}

        <select
          id={id || name}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {placeholder && (
            <option value="" disabled={!value}>
              {placeholder}
            </option>
          )}
          {options.map((option, index) => {
            const optionValue = typeof option === 'string' ? option : option.value;
            const optionLabel = typeof option === 'string' ? option : option.label;
            const optionStyle = typeof option === 'object' ? option.style : undefined;
            const optionClassName = typeof option === 'object' ? option.className : undefined;

            return (
              <option 
                key={optionValue || index} 
                value={optionValue}
                style={optionStyle}
                className={optionClassName}
              >
                {optionLabel}
              </option>
            );
          })}
        </select>

        {/* Custom dropdown arrow */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`text-gray-400 ${iconSizes[size]}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};

export default CustomSelect;